import type { Grounding } from "./confidence.js";
import type { ToolCallSummary } from "./tools.js";

/**
 * Server-sent events streamed from `POST /api/chat`.
 *
 * Every event is one SSE `data:` line holding this JSON. Tool progress is a
 * first-class event so Directo slowness shows as visible activity rather than a
 * frozen page (blueprint §9).
 */
export type ServerEvent =
  | { type: "message.start"; conversationId: string; userMessageId: string; assistantMessageId: string }
  | { type: "token"; text: string }
  | { type: "tool.start"; id: string; tool: string; label: string; args: Record<string, unknown> }
  | { type: "tool.end"; call: ToolCallSummary }
  | { type: "grounding"; grounding: Grounding }
  | { type: "message.end"; messageId: string; grounding: Grounding; usage: TokenUsage; toolCalls: ToolCallSummary[] }
  | { type: "error"; message: string; code?: string };

export type ServerEventType = ServerEvent["type"];

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export const EMPTY_USAGE: TokenUsage = { inputTokens: 0, outputTokens: 0, totalTokens: 0 };

/** Encode one event as an SSE frame. */
export function encodeServerEvent(event: ServerEvent): string {
  return `event: ${event.type}\ndata: ${JSON.stringify(event)}\n\n`;
}

/**
 * Decode one SSE frame (the text between two blank lines). Returns null for
 * heartbeat comments and anything unparseable, so a stray frame cannot kill a
 * stream mid-answer. Kept next to the encoder so the two stay symmetrical.
 */
export function parseServerEventFrame(frame: string): ServerEvent | null {
  const dataLines: string[] = [];

  for (const line of frame.split("\n")) {
    if (line.startsWith(":")) continue; // heartbeat comment
    if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
  }

  if (dataLines.length === 0) return null;

  try {
    return JSON.parse(dataLines.join("\n")) as ServerEvent;
  } catch {
    return null;
  }
}
